import React from 'react';
import './ProgressIndicator.css';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * ProgressIndicator component
 * Displays progress of credit plan steps with a visual progress bar and text
 * @param {Object} tree - The credit plan tree structure
 */
const ProgressIndicator = ({ tree }) => {
  const t = useTranslation();
  // Calculate progress - only count nodes that are actually in the flow (reachable from 'start')
  const calculateProgress = () => {
    if (!tree || Object.keys(tree).length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    // Walk the tree starting from 'start' to find all reachable nodes
    // This matches how useCreditPlanFlowNodes creates the visible nodes
    const visitedNodes = new Set();
    const reachableSteps = [];

    function walkTree(nodeId) {
      // Skip if already visited or doesn't exist
      if (visitedNodes.has(nodeId) || !tree[nodeId]) {
        return;
      }
      
      visitedNodes.add(nodeId);
      const node = tree[nodeId];
      
      // Only count nodes that are not 'start' (exclude the root node)
      if (nodeId !== 'start') {
        reachableSteps.push({ id: nodeId, status: node.status || 'standby' });
      }
      
      // Recursively walk children
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(childId => {
          walkTree(childId);
        });
      }
    }

    // Start walking from 'start' node
    if (tree['start']) {
      walkTree('start');
    } else {
      // If no 'start' node, try to find the first available node
      const firstKey = Object.keys(tree)[0];
      if (firstKey) {
        walkTree(firstKey);
      }
    }

    const total = reachableSteps.length;
    
    // Count completed steps - check for 'done' status (case-insensitive and trimmed)
    const completed = reachableSteps.filter(step => {
      const status = (step.status || '').toString().toLowerCase().trim();
      return status === 'done';
    }).length;
    
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { completed, total, percentage };
  };

  const { completed, total, percentage } = calculateProgress();

  // Don't render if there are no steps
  if (total === 0) {
    return null;
  }

  return (
    <div className="progress-indicator">
      <div className="progress-text">
        <span className="progress-count">{completed}/{total}</span>
        <span className="progress-label">{t('creditPlanFlow.stepsCompleted')}</span>
      </div>
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${completed} ${t('creditPlanFlow.outOf')} ${total} ${t('creditPlanFlow.stepsCompleted').toLowerCase()}`}
        >
          <div className="progress-bar-glow"></div>
        </div>
      </div>
      <div className="progress-percentage">{percentage}%</div>
    </div>
  );
};

export default ProgressIndicator;

